import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { getfinancialyears, getmigrationcount, migrateloanstoaccounting } from "../../../../services/admin";

export default function MigrateLoanData() {
    const [financialYears, setFinancialYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState("");
    const [loading, setLoading] = useState(false);
    const [migrationStats, setMigrationStats] = useState(null);
    const [progress, setProgress] = useState({ current: 0, total: 0, phase: '' });

    useEffect(() => {
        fetchFinancialYears();
    }, []);

    const fetchFinancialYears = () => {
        getfinancialyears().then((res) => {
            if (res.status === true) {
                setFinancialYears(res.data || []);
            }
        });
    };

    const processBatch = async (offset, totalRecords, accumulatedStats, processType, phaseName) => {
        const branchId = localStorage.getItem("branch");
        const data = {
            financial_year_id: selectedYear,
            branch_id: branchId,
            batch_size: 50,
            offset: offset,
            process_type: processType
        };

        console.log('🔵 Calling migration API with data:', data);

        try {
            // Add a small delay to avoid rate limiting (500ms between requests)
            if (offset > 0) {
                await new Promise(resolve => setTimeout(resolve, 500));
            }

            const res = await migrateloanstoaccounting(data);
            console.log('🟢 Migration API response:', res);

            if (res.status === true) {
                const processed = processType === 'loans' ? res.data.loans_processed : res.data.collections_processed;
                const newProcessed = offset + processed;
                setProgress({ current: newProcessed, total: totalRecords, phase: phaseName });

                // Update progress in Swal
                Swal.update({
                    html: `
                        <div style="text-align: center;">
                            <p style="font-size: 16px; margin-bottom: 10px;">${phaseName}...</p>
                            <p style="font-size: 24px; font-weight: bold; color: #3085d6;">
                                ${newProcessed} of ${totalRecords}
                            </p>
                            <div style="width: 100%; background-color: #e0e0e0; border-radius: 10px; height: 20px; margin: 20px 0;">
                                <div style="width: ${(newProcessed / totalRecords * 100).toFixed(1)}%; background-color: #3085d6; height: 100%; border-radius: 10px; transition: width 0.3s;"></div>
                            </div>
                            <p style="font-size: 14px; color: #666;">Please wait...</p>
                        </div>
                    `
                });

                // Accumulate stats
                if (processType === 'loans') {
                    accumulatedStats.loans_processed += (res.data.loans_processed || 0);
                    accumulatedStats.loans_skipped += (res.data.loans_skipped || 0);
                    accumulatedStats.total_loan_amount += (res.data.total_loan_amount || 0);
                } else {
                    accumulatedStats.collections_processed += (res.data.collections_processed || 0);
                    accumulatedStats.collections_skipped += (res.data.collections_skipped || 0);
                    accumulatedStats.total_collection_amount += (res.data.total_collection_amount || 0);
                }

                // Safely handle errors array
                if (res.data.errors && Array.isArray(res.data.errors)) {
                    accumulatedStats.errors.push(...res.data.errors);
                }

                // Process next batch if more data exists
                if (res.data.has_more) {
                    return await processBatch(res.data.next_offset, totalRecords, accumulatedStats, processType, phaseName);
                } else {
                    return accumulatedStats;
                }
            } else {
                console.log('🔴 Migration API returned false status:', res);
                throw new Error(res.message || "Migration failed");
            }
        } catch (error) {
            console.error('🔴 Migration API error:', error);
            throw error;
        }
    };

    const handleMigrate = async () => {
        if (!selectedYear) {
            toast.error("Please select a financial year");
            return;
        }

        const confirmResult = await Swal.fire({
            title: 'Are you sure?',
            html: `
                <div style="text-align: left;">
                    <p>This will migrate historical loan data to the accounting system:</p>
                    <ul>
                        <li>Create vouchers for all loan disbursements</li>
                        <li>Create vouchers for all loan collections</li>
                        <li>Generate ledger entries for selected financial year</li>
                    </ul>
                    <p style="color: #e74c3c; font-weight: bold;">This process may take several minutes and cannot be undone!</p>
                </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, Start Migration!',
            cancelButtonText: 'Cancel'
        });

        if (confirmResult.isConfirmed) {
            try {
                setLoading(true);
                setMigrationStats(null);
                setProgress({ current: 0, total: 0, phase: '' });

                // Get total count first
                const branchId = localStorage.getItem("branch");
                const countData = {
                    financial_year_id: selectedYear,
                    branch_id: branchId
                };
                const countRes = await getmigrationcount(countData);

                console.log('🔍 Count API Response:', countRes);

                if (!countRes.status || !countRes.data || (countRes.data.total_loans === 0 && countRes.data.total_collections === 0)) {
                    setLoading(false);
                    Swal.fire({
                        title: 'No Data!',
                        text: "No loans or collections found for the selected financial year",
                        icon: 'info',
                        confirmButtonText: 'OK'
                    });
                    return;
                }

                const totalLoans = countRes.data.total_loans || 0;
                const totalCollections = countRes.data.total_collections || 0;

                console.log('📊 Starting migration - Loans:', totalLoans, 'Collections:', totalCollections);

                // Show progress modal
                Swal.fire({
                    title: 'Migration in Progress',
                    html: `
                        <div style="text-align: center;">
                            <p style="font-size: 16px; margin-bottom: 10px;">Starting migration...</p>
                            <p style="font-size: 24px; font-weight: bold; color: #3085d6;">
                                0 of ${totalLoans + totalCollections}
                            </p>
                            <div style="width: 100%; background-color: #e0e0e0; border-radius: 10px; height: 20px; margin: 20px 0;">
                                <div style="width: 0%; background-color: #3085d6; height: 100%; border-radius: 10px;"></div>
                            </div>
                            <p style="font-size: 14px; color: #666;">Please wait...</p>
                        </div>
                    `,
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                // Start batch processing - Phase 1: Loans
                const accumulatedStats = {
                    loans_processed: 0,
                    loans_skipped: 0,
                    total_loan_amount: 0,
                    collections_processed: 0,
                    collections_skipped: 0,
                    total_collection_amount: 0,
                    errors: []
                };

                // Process loan disbursements first
                if (totalLoans > 0) {
                    await processBatch(0, totalLoans, accumulatedStats, 'loans', 'Processing loan disbursements');
                }

                // Process collections second
                if (totalCollections > 0) {
                    await processBatch(0, totalCollections, accumulatedStats, 'collections', 'Processing loan collections');
                }

                setLoading(false);
                setMigrationStats(accumulatedStats);

                Swal.fire({
                    title: 'Success!',
                    html: `
                        <p>Migration completed successfully!</p>
                        ${accumulatedStats.loans_processed > 0 ? `<p><strong>Loans Processed:</strong> ${accumulatedStats.loans_processed}</p>` : ''}
                        ${accumulatedStats.loans_skipped > 0 ? `<p><strong>Loans Skipped:</strong> ${accumulatedStats.loans_skipped} (already migrated)</p>` : ''}
                        ${accumulatedStats.collections_processed > 0 ? `<p><strong>Collections Processed:</strong> ${accumulatedStats.collections_processed}</p>` : ''}
                        ${accumulatedStats.collections_skipped > 0 ? `<p><strong>Collections Skipped:</strong> ${accumulatedStats.collections_skipped} (already migrated)</p>` : ''}
                    `,
                    icon: 'success',
                    confirmButtonText: 'OK'
                });

            } catch (error) {
                setLoading(false);
                Swal.fire({
                    title: 'Error!',
                    text: "Migration failed: " + (error.message || "Unknown error"),
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
            }
        }
    };

    return (
        <div>
            <div className="page-header">
                <h2 className="main-content-title tx-24 mg-b-5">
                    Migrate Loan Data to Accounting
                </h2>
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <a>Settings</a>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                        Migrate Loan Data
                    </li>
                </ol>
            </div>

            <div className="row">
                <div className="col-md-8 offset-md-2">
                    <div className="card">
                        <div className="card-header">
                            <h4 className="card-title">Historical Data Migration</h4>
                        </div>
                        <div className="card-body">
                            <div className="alert alert-info">
                                <i className="fa fa-info-circle"></i> This tool will migrate your historical loan data into the accounting system for the current branch. It will create:
                                <ul className="mt-2 mb-0">
                                    <li>Payment vouchers for loan disbursements</li>
                                    <li>Receipt vouchers for loan collections</li>
                                    <li>Ledger entries for Cash, Loan Receivable, and Interest Income accounts</li>
                                </ul>
                                <p className="mt-2 mb-0"><strong>Note:</strong> Only data for your current branch will be migrated.</p>
                            </div>

                            {loading && progress.total > 0 && (
                                <div className="alert alert-warning">
                                    <i className="fa fa-spinner fa-spin"></i> Migration in progress... Processing {progress.current} of {progress.total} collections. Please wait.
                                </div>
                            )}

                            <div className="mb-3">
                                <label className="form-label">
                                    Select Financial Year <span className="text-danger">*</span>
                                </label>
                                <select
                                    className="form-control"
                                    value={selectedYear}
                                    onChange={(e) => setSelectedYear(e.target.value)}
                                    disabled={loading}
                                >
                                    <option value="">Select Financial Year</option>
                                    {financialYears.map((year) => (
                                        <option key={year.id} value={year.id}>
                                            {year.year_name} 
                                            {/* ({year.start_date} to {year.end_date}) */}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="text-end">
                                <button
                                    className="btn btn-primary"
                                    onClick={handleMigrate}
                                    disabled={loading || !selectedYear}
                                >
                                    <i className="fa fa-database"></i> {loading ? 'Migrating...' : 'Start Migration'}
                                </button>
                            </div>

                            {migrationStats && (
                                <div className="mt-4">
                                    <hr />
                                    <h5>Migration Results</h5>
                                    <div className="table-responsive">
                                        <table className="table table-bordered">
                                            <tbody>
                                                {migrationStats.loans_processed > 0 && (
                                                    <>
                                                        <tr>
                                                            <td><strong>Loans Processed:</strong></td>
                                                            <td>{migrationStats.loans_processed}</td>
                                                        </tr>
                                                        {migrationStats.loans_skipped > 0 && (
                                                            <tr>
                                                                <td><strong>Loans Skipped:</strong></td>
                                                                <td>
                                                                    <span className="badge bg-warning">{migrationStats.loans_skipped}</span>
                                                                    <small className="text-muted ms-2">(already migrated)</small>
                                                                </td>
                                                            </tr>
                                                        )}
                                                        <tr>
                                                            <td><strong>Total Loan Amount:</strong></td>
                                                            <td>₹ {parseFloat(migrationStats.total_loan_amount || 0).toFixed(2)}</td>
                                                        </tr>
                                                    </>
                                                )}
                                                {migrationStats.collections_processed > 0 && (
                                                    <>
                                                        <tr>
                                                            <td><strong>Collections Processed:</strong></td>
                                                            <td>{migrationStats.collections_processed}</td>
                                                        </tr>
                                                        {migrationStats.collections_skipped > 0 && (
                                                            <tr>
                                                                <td><strong>Collections Skipped:</strong></td>
                                                                <td>
                                                                    <span className="badge bg-warning">{migrationStats.collections_skipped}</span>
                                                                    <small className="text-muted ms-2">(already migrated)</small>
                                                                </td>
                                                            </tr>
                                                        )}
                                                        <tr>
                                                            <td><strong>Total Collection Amount:</strong></td>
                                                            <td>₹ {parseFloat(migrationStats.total_collection_amount || 0).toFixed(2)}</td>
                                                        </tr>
                                                    </>
                                                )}
                                                {migrationStats.errors && migrationStats.errors.length > 0 && (
                                                    <tr>
                                                        <td><strong>Errors:</strong></td>
                                                        <td>
                                                            <div className="text-danger">
                                                                {migrationStats.errors.length} errors occurred
                                                                <details className="mt-2">
                                                                    <summary>View Errors</summary>
                                                                    <ul className="mt-2">
                                                                        {migrationStats.errors.map((error, index) => (
                                                                            <li key={index}>{error}</li>
                                                                        ))}
                                                                    </ul>
                                                                </details>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="alert alert-success">
                                        <i className="fa fa-check-circle"></i> Migration completed! You can now view the generated vouchers and ledger entries in the accounting reports.
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
